import { Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"

import {
    CORE_MANUFACTURER_REG,
    registerDeviceLabelMapping,
} from "../../../../../constants/ttr/registerDevice"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import { AppCard } from "../../../../surfaces/appCard"
import { MANU_SUBTITLE } from "../../../common/labels"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { TTRDeviceFormData } from "../../types/providerTypes"

const mapRegisterNameToValues = (
    formData: TTRDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [CORE_MANUFACTURER_REG]: formData.coreDetails.manufacturer,
})

interface IManufacturerProps {
    context: Context<IDeviceFormContextValue<TTRDeviceFormData>>
}

/** Only displayed to sys admins. Allows the user to specify the manufacturer
 * of the device they intend to install
 */
const Manufacturer = ({ context }: IManufacturerProps): ReactElement => {
    const { currentIndex, setCurrentIndex, formData, setFormData } =
        useContext(context)

    const { manufacturerMappings, manufacturerIds, isLoading } =
        useFilterReferenceMappings()

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: mapRegisterNameToValues(formData),
        values: mapRegisterNameToValues(formData),
        shouldUnregister: true,
    })

    const updateManufacturer = (): void => {
        formData.coreDetails.manufacturer = watch(
            CORE_MANUFACTURER_REG
        ) as number
        setFormData({ ...formData })
    }

    const onClickNext = (): void => {
        updateManufacturer()
        setCurrentIndex(currentIndex + 1)
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Manufacturer"
                    subHeader={MANU_SUBTITLE}
                    index={currentIndex + 1}
                />
                <ValidatedAutocomplete
                    control={control}
                    required
                    registerName={CORE_MANUFACTURER_REG}
                    label={registerDeviceLabelMapping[CORE_MANUFACTURER_REG]}
                    options={manufacturerIds}
                    getOptionLabel={(option) => manufacturerMappings[option]}
                    loading={isLoading}
                />
                <FormErrors
                    errors={errors}
                    getFriendlyName={(key: string) =>
                        registerDeviceLabelMapping[key]
                    }
                />
                <FormNavigation
                    onClickNext={() => handleSubmit(onClickNext)()}
                    nextText="Next"
                    nextDisabled={isLoading}
                />
            </Stack>
        </AppCard>
    )
}

export default Manufacturer
